import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useStore } from "@/lib/store";
import { useXC, useSlice } from "@/lib/hooks";
import { asList } from "@/lib/xc";
import CategoryRail from "@/components/CategoryRail";
import PosterCard from "@/components/PosterCard";
import MovieModal from "@/components/MovieModal";
import SeriesModal from "@/components/SeriesModal";

const CFG = {
  movie: {
    title: "Movies",
    cats: "get_vod_categories",
    list: "get_vod_streams",
    map: (s) => ({ type: "movie", id: s.stream_id, name: s.name, icon: s.stream_icon, rating: s.rating, ext: s.container_extension }),
  },
  series: {
    title: "Series",
    cats: "get_series_categories",
    list: "get_series",
    map: (s) => ({ type: "series", id: s.series_id, name: s.name, icon: s.cover, rating: s.rating, sub: s.genre }),
  },
};

export default function Library({ kind }) {
  const cfg = CFG[kind];
  const { active, isFav, toggleFav, pct } = useStore();
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const cats = useXC(active, cfg.cats);
  const items = useXC(active, cfg.list, cat ? { category_id: cat } : {});
  const list = useMemo(() => {
    const all = asList(items.data).map(cfg.map);
    return q ? all.filter((m) => m.name?.toLowerCase().includes(q.toLowerCase())) : all;
  }, [items.data, q, cfg]);
  const [visible, onScroll] = useSlice(list, 60);
  const catName = asList(cats.data).find((c) => c.category_id === cat)?.category_name || "All";

  return (
    <div className="flex h-full" data-testid={`${kind}-page`}>
      <CategoryRail categories={asList(cats.data)} active={cat} onSelect={setCat} loading={cats.loading} testPrefix={kind} />
      <section className="flex-1 min-w-0 overflow-y-auto xc-scroll" onScroll={onScroll}>
        <div className="sticky top-0 z-10 bg-[#08090E]/85 backdrop-blur-md border-b border-[#212738] px-8 py-5 flex items-end gap-6">
          <div className="flex-1 min-w-0">
            <div className="xc-label">{cfg.title}</div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight truncate">{catName}</h1>
          </div>
          <span className="xc-label pb-1.5">{list.length} titles</span>
          <div className="relative w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${cfg.title.toLowerCase()}`} className="xc-input !pl-9" data-testid={`${kind}-search-input`} />
          </div>
        </div>
        <div className="p-8">
          {items.loading && <div className="text-sm text-slate-500">Loading…</div>}
          {items.error && <div className="text-sm text-[#FF5A62]">{items.error}</div>}
          {!items.loading && !list.length && !items.error && <div className="text-sm text-slate-500">Nothing here.</div>}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-5" data-testid={`${kind}-grid`}>
            {visible.map((m) => (
              <PosterCard
                key={m.id}
                title={m.name}
                image={m.icon}
                rating={m.rating}
                sub={m.sub}
                progress={pct(`${kind}:${m.id}`)}
                fav={isFav(kind, m.id)}
                onFav={() => toggleFav(m)}
                onClick={() => setOpen(m)}
                testId={`${kind}-card-${m.id}`}
              />
            ))}
          </div>
        </div>
      </section>
      {kind === "movie" && open && <MovieModal movie={open} onClose={() => setOpen(null)} />}
      {kind === "series" && open && <SeriesModal series={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
